import { CheckSquareFilled, ClockCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { MenuProps, Switch } from 'antd';
import { AnyObject } from 'antd/es/_util/type';
import { TableRowSelection } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WorkspaceAccessResponse } from '../../api/workspace';
import { SprintIcon } from '../../assets/CustomIcons/SprintIcon';
import { getWebsiteDomain } from '../../authConfig';
import { ModalContext, ModalType } from '../../context/ModalContext';
import { buildAddSubitemModalProps } from '../Modal/createItemSpace';
import { useIsSmallerThanTableWidth } from '../../hooks/useIsMobile';
import { useNavigateToTask } from '../../hooks/useTaskNavigation';
import { useViewContext } from '../../pages/Space/Context/ViewContext';
import { useCurrentWorkspaceEpics } from '../../serverState/epics';
import { useGetWorkspace } from '../../serverState/workspace';
import Task from '../../types/Task';
import { defaultTaskStatuses, TaskStatus, TaskStatusGroup } from '../../types/TaskStatus';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../types/Workspace';
import { WorkspaceViewEntityType, WorkspaceViewGroup } from '../../types/WorkspaceView';
import { Column } from '../../types/WorkspaceViewColumn';
import { EpicLabelWithGoToIcon } from '../UI/Dropdown/EpicDropdown';
import { PriorityLabel } from '../UI/Dropdown/PriorityDropdown';
import LabelChip, { LabelChipType } from '../UI/LabelChip';
import ProgressLabel from '../UI/ProgressLabel';
import { SprintStatusLabel } from '../UI/SprintStatusLabel';
import StatusLabel, { StatusComponentType } from '../UI/StatusLabel';
import { TaskEstimateComponentType, TaskEstimateLabel } from '../UI/TaskEstimateLabel';
import CustomTooltip from '../UI/Tooltip/CustomTooltip';
import { getEffectiveSprintStatus } from '../../utils/taskListUtils';
import { buildTaskUrl } from '../../utils/urlUtils';
import { COLUMN_WIDTHS as BASE_COLUMN_WIDTHS } from './constants/columnWidths';
import { PriorityOptions, UserAvatar } from './helpers/helper';
import { RowMenuDropdown } from './RowMenuDropdown';
import './TaskTable.scss';

// Task view's subtask table: 360px for the name text + 24px for the task type icon
// rendered alongside it (#2671). A further icon column (comments/attachments/links/
// description indicators) is planned but out of scope for this width.
const TASK_NAME_COLUMN_WIDTH = 384;

/**
 * Status ids are per-workspace. A row carrying an id this workspace doesn't know is a
 * data problem, not a "To Do" — rendering the default made a foreign subitem look like
 * a normal one with the wrong status. (#2881)
 */
export const resolveRowStatus = (
  taskStatuses: Record<TaskStatus['id'], TaskStatus>,
  statusId: number
): TaskStatus | null => taskStatuses[statusId] ?? null;

const TruncatedTitleCell = ({ title }: { title: string }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const titleTextRef = useRef<HTMLDivElement>(null);

  return (
    <CustomTooltip title={isTruncated ? title : ''}>
      <div
        ref={titleTextRef}
        className="truncate overflow-hidden whitespace-nowrap py-[4px] px-[8px] rounded"
        onMouseEnter={() => {
          if (titleTextRef.current) {
            setIsTruncated(titleTextRef.current.scrollWidth > titleTextRef.current.clientWidth);
          }
        }}
      >
        {title}
      </div>
    </CustomTooltip>
  );
};

interface TaskTableColumnsProps {
  dataSource: Task[];
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  showMultiselect: boolean;
  selectedItems: number[];
  setSelectedItems: (items: number[]) => void;
  rowSelection?: TableRowSelection<AnyObject>;
  customRowSelection:
    | {
        selectedRowKeysObject: { [id: number]: boolean };
        addTaskToSelectedKeys: (task: Task) => void;
        removeTaskFromSelectedKeys: (task: Task) => void;
      }
    | undefined;
  rowMenuProps: (record: any) => MenuProps;
  filterColumns: Column[];
  showMessage: (message: string) => void;
  showSubitems: boolean;
}

export const TaskTableColumns = (props: TaskTableColumnsProps) => {
  const {
    dataSource,
    usersWithAccessToSpace,
    showMultiselect,
    selectedItems,
    setSelectedItems,
    rowSelection,
    customRowSelection,
    rowMenuProps,
    filterColumns,
    showMessage,
    showSubitems,
  } = props;

  const isSmallerThanTableWidth = useIsSmallerThanTableWidth();
  const { orgId, spaceId } = useParams<{ orgId: string; spaceId: string }>();
  const workspaceQuery = useGetWorkspace();
  const navigateToTask = useNavigateToTask();
  const epicsQuery = useCurrentWorkspaceEpics();
  const { openModal } = useContext(ModalContext);

  const epics = epicsQuery.data || [];
  const framework = workspaceQuery.data?.framework;
  const taskIds = [
    ...new Set(dataSource.map((task) => task.id)),
    ...(showSubitems ? new Set(dataSource.flatMap((task) => task.subtasks?.map((subtask) => subtask.id) || [])) : []),
  ];
  const allTasksChecked = taskIds.length > 0 ? taskIds.every((taskId) => selectedItems.includes(taskId)) : false;
  const taskStatuses: Record<TaskStatus['id'], TaskStatus> = (
    (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
  ).reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});

  const viewContext = useViewContext();
  const view = viewContext?.view || undefined;
  const columnsOrder = view?.columns?.order;
  const hiddenColumns = view?.columns?.hidden;
  const estimationType = workspaceQuery.data?.estimationType || WorkspaceEstimationType.storyPoints;

  // Track Shift key state to prevent text selection during range selection
  useEffect(() => {
    if (!showMultiselect) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        document.body.style.userSelect = 'none';
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        document.body.style.userSelect = '';
      }
    };

    const handleWindowBlur = () => {
      // Reset selection when window loses focus (handles edge cases)
      document.body.style.userSelect = '';
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
      // Cleanup CSS on unmount
      document.body.style.userSelect = '';
    };
  }, [showMultiselect]);
  const COLUMN_WIDTHS = {
    ...BASE_COLUMN_WIDTHS,
    operation: rowSelection ? 86 : 68,
  };

  const getTableWidthWithoutTaskName = () => {
    const columns = Object.entries(COLUMN_WIDTHS).map(([dataIndex, width]) => ({
      dataIndex,
      width,
    }));
    const frameworkFilteredColumns =
      framework === WorkspaceFramework.none ||
      view?.group === WorkspaceViewGroup.sprint ||
      view?.group === WorkspaceViewGroup.kanban
        ? columns.filter((column) => column.dataIndex !== 'sprintId')
        : columns;
    const filteredColumns = hiddenColumns
      ? frameworkFilteredColumns.filter((column) => {
          const mappedColumn = mapToColumn(column.dataIndex);
          return mappedColumn ? !hiddenColumns.includes(mappedColumn) : true;
        })
      : frameworkFilteredColumns;
    return filteredColumns.reduce((acc, column) => {
      return acc + (column.width || 0);
    }, 0);
  };

  return useMemo(() => {
    const columns = [
      {
        title: () => (
          <div className="flex items-center" style={{ paddingLeft: '36px' }}>
            {showMultiselect && allTasksChecked && (
              <CheckSquareFilled
                style={{ fontSize: '16px' }}
                className="text-blue-500"
                onClick={() =>
                  setSelectedItems([...new Set(selectedItems.filter((itemId) => !taskIds.includes(itemId)))])
                }
              />
            )}
            {showMultiselect && taskIds.length > 0 && !allTasksChecked && (
              <div
                className={`${selectedItems.length > 0 ? '' : 'epic-section__check'} h-4 w-4 rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500`}
                onClick={() => setSelectedItems([...new Set([...selectedItems, ...taskIds])])}
              />
            )}
          </div>
        ),
        dataIndex: 'operation',
        // specify if in planning it will be 86 px
        width: `${COLUMN_WIDTHS.operation}px`,
        fixed: 'left',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        render: (_, record) => {
          const isInSprint = record.sprintId && record.sprintId === workspaceQuery.data?.activeSprintId;
          return (
            <div style={{ width: `${COLUMN_WIDTHS.operation - 16}px` }} className={`flex items-center justify-between`}>
              <RowMenuDropdown menuProps={rowMenuProps(record)} className="operation_items ml-2" />
              {showMultiselect && selectedItems.includes(record.id) && (
                <CheckSquareFilled
                  style={{ fontSize: '16px' }}
                  className="text-blue-500"
                  onClick={(e) => {
                    if (e.shiftKey && selectedItems.length > 0) {
                      e.preventDefault();
                      // When shift is held, only allow adding to selection, never deselecting
                      const currentIndex = taskIds.indexOf(record.id);
                      const lastSelectedIndex = taskIds.indexOf(selectedItems[selectedItems.length - 1]);
                      const start = Math.min(currentIndex, lastSelectedIndex);
                      const end = Math.max(currentIndex, lastSelectedIndex);
                      const rangeIds = taskIds.slice(start, end + 1);
                      setSelectedItems([...new Set([...selectedItems, ...rangeIds])]);
                    } else {
                      setSelectedItems([...new Set(selectedItems.filter((itemId) => itemId !== record.id))]);
                    }
                  }}
                />
              )}
              {showMultiselect && !selectedItems.includes(record.id) && (
                <div
                  className={`${selectedItems.length > 0 ? '' : 'operation_items'} h-4 w-4 rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500`}
                  onClick={(e) => {
                    if (e.shiftKey && selectedItems.length > 0) {
                      e.preventDefault();
                      const currentIndex = taskIds.indexOf(record.id);
                      const lastSelectedIndex = taskIds.indexOf(selectedItems[selectedItems.length - 1]);
                      const start = Math.min(currentIndex, lastSelectedIndex);
                      const end = Math.max(currentIndex, lastSelectedIndex);
                      const rangeIds = taskIds.slice(start, end + 1);
                      setSelectedItems([...new Set([...selectedItems, ...rangeIds])]);
                    } else {
                      setSelectedItems([...new Set([...selectedItems, record.id])]);
                    }
                  }}
                />
              )}

              {customRowSelection && (customRowSelection.selectedRowKeysObject[record.id] || false) && (
                <CustomTooltip title={'Click to remove from Planning'}>
                  <div
                    className={`h-4 w-4 flex items-center justify-center rounded-sm bg-blue-500`}
                    onClick={(e) => {
                      e.preventDefault();
                      customRowSelection.removeTaskFromSelectedKeys(record);
                    }}
                  >
                    <ClockCircleOutlined className="w-3 h-3 text-white" />
                  </div>
                </CustomTooltip>
              )}

              {customRowSelection && !(customRowSelection.selectedRowKeysObject[record.id] || false) && (
                <CustomTooltip title={isInSprint ? 'Click to move to Planning' : 'Click to add to Planning'}>
                  <div
                    className={`h-4 w-4 flex items-center justify-center rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500 ${isInSprint ? 'bg-yellow-200 border-yellow-200 hover:border-yellow-200' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      customRowSelection.addTaskToSelectedKeys(record);
                    }}
                  >
                    {isInSprint && <SprintIcon className="w-3 h-3 text-yellow-600" />}
                  </div>
                </CustomTooltip>
              )}
            </div>
          );
        },
      },
      {
        title: () => <div>ID</div>,
        dataIndex: 'taskId',
        editable: false,
        width: `${COLUMN_WIDTHS.taskId}px`,
        fixed: 'left',
        className: rowSelection ? 'custom-planning-taskId-cell' : 'custom-taskId-cell',
        render: (title: string) => {
          return (
            <div className="w-full flex items-center justify-between taskId_container">
              <div className={'text-neutral-900'}>{`DEV-${title}`}</div>
              <div
                className="w-5 h-5 bg-white flex items-center justify-center rounded taskId_link"
                onClick={async () => {
                  // Build from route params, not location.pathname: this table renders inside
                  // the task-detail view (URL ends in /task/{parentId}), so the old pathname
                  // surgery left a dangling "task" and produced /task/task/{id} (#2887).
                  const taskUrl = buildTaskUrl({ orgId: orgId ?? '', spaceId: spaceId ?? '', taskId: title });
                  await navigator.clipboard.writeText(`${getWebsiteDomain()}${taskUrl}`);
                  showMessage('Task link copied!');
                }}
              >
                <LinkOutlined className="text-neutral-900" />
              </div>
            </div>
          );
        },
      },
      ...(showSubitems
        ? [
            {
              title: '',
              dataIndex: 'expand',
              width: '20px',
              fixed: 'left',
              className: rowSelection ? 'custom-planning-subitems-cell' : 'custom-subitems-cell',
              render: () => {
                return <div className="flex items-center justify-center"></div>;
              },
            },
          ]
        : []),
      {
        title: () => (
          <div className="group flex flex-row items-center justify-start ">
            <div className="ml-[7px] relative">{'Task name'}</div>
          </div>
        ),
        dataIndex: 'title',
        editable: true,
        width:
          filterColumns.length > 0
            ? // Task view's subtask table: fixed at 360px for the name text + 24px for the
              // task type icon rendered alongside it (#2671).
              `${TASK_NAME_COLUMN_WIDTH}px`
            : window.innerWidth - 255 - getTableWidthWithoutTaskName() < 394
              ? '394px'
              : undefined,
        fixed: 'left',
        className: showSubitems
          ? rowSelection
            ? 'custom-planning-taskName-adjusted-cell'
            : 'custom-taskName-adjusted-cell'
          : rowSelection
            ? 'custom-planning-taskName-cell'
            : 'custom-taskName-cell',
        render: (title: string) => {
          return <TruncatedTitleCell title={title} />;
        },
      },
      {
        title: () => (
          <div className="flex justify-center">
            {view?.entityType === WorkspaceViewEntityType.epic ? 'Progress' : 'Subitems'}
          </div>
        ),
        dataIndex: 'subtasks',
        editable: false,
        width: `${COLUMN_WIDTHS.subitems}px`,
        onCell: (record: Task) => ({
          onClick: record.parentTaskId
            ? undefined
            : (e: React.MouseEvent) => {
                e.stopPropagation();
                openModal(ModalType.CreateItem, buildAddSubitemModalProps(record));
              },
          style: record.parentTaskId ? undefined : { cursor: 'pointer' },
        }),
        render: (subtasks: Task[], record: Task) => {
          if (!subtasks) {
            return <div />;
          }
          const completedSubtasks = subtasks.filter(
            (subtask) => taskStatuses[subtask.statusId]?.group === TaskStatusGroup.completed
          );
          return (
            <ProgressLabel task={record} completedSubtasks={completedSubtasks.length} subtasks={subtasks.length} />
          );
        },
      },
      {
        title: () => <div className="flex justify-center"> {'Epic'}</div>,
        dataIndex: 'epicId',
        editable: true,
        width: `${COLUMN_WIDTHS.epicId}px`,
        render: (epicId: string, record: Task) => {
          const epic = epics.find((p) => p.id === Number(epicId));
          return (
            <CustomTooltip
              title={record.parentTaskId ? 'Epic is inherited from the parent Item and can not be changed.' : ''}
            >
              <div
                className={`w-[150px] flex justify-center items-center ${record.parentTaskId ? 'cursor-not-allowed' : ''}`}
              >
                {!epic && <div className="text-neutral-700 p-1">No epic</div>}

                {epic && (
                  <EpicLabelWithGoToIcon
                    epic={epic}
                    size="medium"
                    disabled={record.parentTaskId}
                    onOpenEpic={() => navigateToTask(epic)}
                    className="h-[35px]"
                  />
                )}
              </div>
            </CustomTooltip>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center">
            <div className="relative">Stage</div>
          </div>
        ),
        dataIndex: 'sprintId',
        editable: true,
        width: `${COLUMN_WIDTHS.sprintId}px`,
        render: (_, data) => {
          const task = data as Task;
          return (
            <SprintStatusLabel
              sprintStatus={getEffectiveSprintStatus({
                task,
                isScrum: framework === WorkspaceFramework.scrum,
                activeSprintId: workspaceQuery.data?.activeSprintId,
              })}
              component="table"
            ></SprintStatusLabel>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center ">
            <div className="mx-1 relative">{'Label'}</div>
          </div>
        ),
        dataIndex: 'TaskLabels',
        editable: true,
        width: `${COLUMN_WIDTHS.TaskLabels}px`,
        render: (taskLabels: any[]) => {
          // Get labels from workspace data
          const workspaceLabels = workspaceQuery.data?.Labels || [];
          const labels =
            (
              taskLabels?.map((tl) => workspaceLabels.find((label) => label.id === tl.labelId)).filter(Boolean) || []
            ).sort((a, b) => {
              return (a?.orderIndex ?? a?.id ?? 0) - (b?.orderIndex ?? b?.id ?? 0);
            }) || [];

          return (
            <div className="w-[125px] flex justify-start items-center p-1 gap-1">
              {labels.length > 0 ? (
                <>
                  {labels[0] && <LabelChip key={labels[0].id} label={labels[0]} type={LabelChipType.Table} />}
                  {labels.length > 1 && (
                    <CustomTooltip
                      title={
                        <div>
                          {labels.slice(1).map((l, index) => (
                            <div key={index}>{l?.title}</div>
                          ))}
                        </div>
                      }
                    >
                      <div className="px-2 py-0.5 bg-neutral-200 rounded flex justify-center items-center">
                        <div className="text-neutral-900 text-sm font-normal font-['Figtree'] leading-tight">
                          +{labels.length - 1}
                        </div>
                      </div>
                    </CustomTooltip>
                  )}
                </>
              ) : (
                <div className="text-neutral-700 px-1">No label</div>
                // <div className="text-neutral-700 p-1 cursor-pointer hover:text-neutral-900 group">
                //   <span className="group-hover:hidden">No label</span>
                //   <span className="hidden group-hover:inline">Add label</span>
                // </div>
              )}
            </div>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center  ">
            <div className="mx-1 relative">{'Owner'}</div>
          </div>
        ),
        dataIndex: 'assignee',
        editable: true,
        width: `${COLUMN_WIDTHS.assignee}px`,
        render: (assignee: number | undefined) => {
          return (
            <div className={'flex justify-center items-center'}>
              <UserAvatar assignee={assignee} usersWithAccessToSpace={usersWithAccessToSpace} />
            </div>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center ">
            <div className="mx-1 relative">{'Status'}</div>
          </div>
        ),
        dataIndex: 'statusId',
        editable: true,
        width: `${COLUMN_WIDTHS.statusId}px`,
        render: (statusId: number) => {
          const status = resolveRowStatus(taskStatuses, statusId);
          if (!status) return <div className="w-[125px]" />;
          return (
            <div className="w-[125px] flex justify-center items-center pl-2">
              <StatusLabel
                color={status.color}
                title={status.title}
                group={status.group}
                component={StatusComponentType.Table}
              />
            </div>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center ">
            <div className="mx-1 relative">{'Start Date'}</div>
          </div>
        ),
        dataIndex: 'startDate',
        editable: true,
        width: `${COLUMN_WIDTHS.startDate}px`,
        render: (date: Date) => {
          let finalDate = date;
          if (typeof date === 'string') {
            finalDate = new Date(date);
          }
          return finalDate ? (
            <div className="text-neutral-900">{dayjs(finalDate).format('MMM DD, YYYY')}</div>
          ) : (
            <div className="text-neutral-700">{'No date'}</div>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center ">
            <div className="mx-1 relative">{'Target Date'}</div>
          </div>
        ),
        dataIndex: 'deadline',
        editable: true,
        width: `${COLUMN_WIDTHS.deadline}px`,
        render: (date: Date) => {
          let finalDate = date;
          if (typeof date === 'string') {
            finalDate = new Date(date);
          }
          return finalDate ? (
            <div className="text-neutral-900">{dayjs(finalDate).format('MMM DD, YYYY')}</div>
          ) : (
            <div className="text-neutral-700">{'No date'}</div>
          );
        },
      },
      {
        title: () => (
          <div className="group flex flex-row items-center justify-center ">
            <div className="mx-1 relative">{'Priority'}</div>
          </div>
        ),
        dataIndex: 'priority',
        editable: true,
        width: `${COLUMN_WIDTHS.priority}px`,
        render: (priority: PriorityOptions) => {
          return <PriorityLabel priority={priority} size="medium" />;
        },
      },
      {
        title: <div className="flex justify-center">Estimate</div>,
        dataIndex: estimationType === WorkspaceEstimationType.tshirtSizes ? 'estimate' : 'storyPoints',
        editable: true,
        width: `${COLUMN_WIDTHS.estimate}px`,
        render: (e: number) => {
          return (
            <div className="flex items-center justify-center p-1">
              <TaskEstimateLabel
                estimate={e}
                component={TaskEstimateComponentType.table}
                estimationType={estimationType}
              />
            </div>
          );
        },
      },
    ];
    const frameworkFilteredColumns =
      framework === WorkspaceFramework.none ||
      view?.group === WorkspaceViewGroup.sprint ||
      view?.group === WorkspaceViewGroup.kanban
        ? columns.filter((column) => column.dataIndex !== 'sprintId')
        : columns;
    const filteredColumns = hiddenColumns
      ? frameworkFilteredColumns.filter((column) => {
          const mappedColumn = mapToColumn(column.dataIndex);
          return mappedColumn ? !hiddenColumns.includes(mappedColumn) : true;
        })
      : frameworkFilteredColumns;

    // if startDate is not in the columnsOrder, add it before deadline
    if (!columnsOrder?.includes(Column.startDate)) {
      columnsOrder?.splice(columnsOrder.indexOf(Column.deadline), 0, Column.startDate);
    }
    // if subitems is not in the columnsOrder, add it to the beginning
    if (!columnsOrder?.includes(Column.subitems)) {
      columnsOrder?.unshift(Column.subitems);
    }
    // if label is not in the columnsOrder, add it before assignee
    if (!columnsOrder?.includes(Column.label)) {
      columnsOrder?.splice(columnsOrder.indexOf(Column.assignee), 0, Column.label);
    }

    const finalColumns = columnsOrder
      ? filteredColumns.sort((a, b) => {
          const aColumn = mapToColumn(a.dataIndex);
          const bColumn = mapToColumn(b.dataIndex);
          const orderA = aColumn ? columnsOrder.indexOf(aColumn) : -1;
          const orderB = bColumn ? columnsOrder.indexOf(bColumn) : -1;
          return orderA - orderB;
        })
      : filteredColumns;

    const spaceColumn = {
      title: '',
      dataIndex: 'space',
      editable: false,
      width: `${COLUMN_WIDTHS.space}px`,
      render: () => {
        return <></>;
      },
    };

    const finalColumnsWithSpace = [...finalColumns, spaceColumn].filter((column) => {
      const mappedColumn = mapToColumn(column.dataIndex);
      return mappedColumn ? !filterColumns.includes(mappedColumn) : true;
    });
    if (view?.entityType === WorkspaceViewEntityType.epic) {
      const nonEpicColumns = [
        Column.sprintStatus,
        Column.epic,
        Column.status,
        Column.priority,
        Column.estimate,
        Column.label,
      ];
      return finalColumnsWithSpace.filter((column) => {
        const mappedColumn = mapToColumn(column.dataIndex);
        return mappedColumn ? !nonEpicColumns.includes(mappedColumn) : true;
      });
    }
    return finalColumnsWithSpace;
  }, [dataSource, selectedItems, viewContext?.view?.columns, isSmallerThanTableWidth]);
};

const mapToColumn = (dataIndex: string): Column | null => {
  switch (dataIndex) {
    case 'sprintId':
      return Column.sprintStatus;
    case 'epicId':
      return Column.epic;
    case 'assignee':
      return Column.assignee;
    case 'statusId':
      return Column.status;
    case 'TaskLabels':
      return Column.label;
    case 'startDate':
      return Column.startDate;
    case 'deadline':
      return Column.deadline;
    case 'priority':
      return Column.priority;
    case 'estimate':
      return Column.estimate;
    case 'storyPoints':
      return Column.estimate;
    case 'subtasks':
      return Column.subitems;
    default:
      return null;
  }
};

const CustomSwitch = ({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) => {
  const handleSwitchChange = async (newChecked: boolean) => {
    onChange(newChecked);
  };

  return <Switch size="small" checked={checked} onChange={handleSwitchChange} />;
};
